import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Palette, Edit2, Save, X } from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';

interface Props {
  clientId: number;
}

export default function BrandIdentityTab({ clientId }: Props) {
  const [client, setClient] = useState<any>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    brand_voice: '',
    brand_colors: '',
    brand_guidelines_url: '',
    brand_fonts: '',
    mission_statement: '',
  });

  useEffect(() => {
    loadData();
  }, [clientId]);

  const loadData = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/clients/${clientId}`);
      setClient(response.data);
      setFormData({
        brand_voice: response.data.brand_voice || '',
        brand_colors: response.data.brand_colors || '',
        brand_guidelines_url: response.data.brand_guidelines_url || '',
        brand_fonts: response.data.brand_fonts || '',
        mission_statement: response.data.mission_statement || '',
      });
    } catch (error) {
      console.error('Failed to load client:', error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API_BASE_URL}/clients/${clientId}`, formData);
      await loadData();
      setEditing(false);
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const parseBrandColors = (colorsJson?: string) => {
    if (!colorsJson) return {};
    try {
      return JSON.parse(colorsJson);
    } catch {
      return {};
    }
  };

  const brandColors = parseBrandColors(editing ? formData.brand_colors : client?.brand_colors);

  if (!client) return <div className="text-center py-12 text-gray-500">Loading...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Palette className="w-5 h-5 text-blue-600" />
          Brand Identity
        </h2>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Edit2 className="w-4 h-4" />
            Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => { setEditing(false); loadData(); }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <X className="w-4 h-4 inline mr-1" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4 inline mr-1" />
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
        {/* Brand Voice */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Brand Voice & Tone</label>
          {editing ? (
            <textarea
              value={formData.brand_voice}
              onChange={(e) => setFormData({ ...formData, brand_voice: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Describe the brand's voice and tone..."
            />
          ) : (
            <p className="text-gray-600">{client.brand_voice || 'Not set'}</p>
          )}
        </div>

        {/* Brand Colors */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Brand Colors (JSON)</label>
          {editing ? (
            <textarea
              value={formData.brand_colors}
              onChange={(e) => setFormData({ ...formData, brand_colors: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              placeholder='{"primary":"#2563eb","secondary":"#7c3aed"}'
            />
          ) : (
            <div className="flex flex-wrap gap-3">
              {Object.entries(brandColors).map(([name, color]) => (
                <div key={name} className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded border border-gray-300" style={{ backgroundColor: color as string }} />
                  <div>
                    <div className="text-xs font-medium text-gray-700 capitalize">{name}</div>
                    <div className="text-xs text-gray-500">{color}</div>
                  </div>
                </div>
              ))}
              {Object.keys(brandColors).length === 0 && <span className="text-gray-500">No colors set</span>}
            </div>
          )}
        </div>

        {/* Brand Guidelines URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Brand Guidelines URL</label>
          {editing ? (
            <input
              type="url"
              value={formData.brand_guidelines_url}
              onChange={(e) => setFormData({ ...formData, brand_guidelines_url: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="https://..."
            />
          ) : (
            client.brand_guidelines_url ? (
              <a href={client.brand_guidelines_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                {client.brand_guidelines_url}
              </a>
            ) : (
              <span className="text-gray-500">Not set</span>
            )
          )}
        </div>

        {/* Mission Statement */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Mission Statement</label>
          {editing ? (
            <textarea
              value={formData.mission_statement}
              onChange={(e) => setFormData({ ...formData, mission_statement: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Company mission statement..."
            />
          ) : (
            <p className="text-gray-600">{client.mission_statement || 'Not set'}</p>
          )}
        </div>
      </div>
    </div>
  );
}